/* Native purchase and pack radios belong to the existing Shopify form.
 * Allocation data supplies prices; the native card owns the selling plan.
 * Dawn and Shopify retain basket and accelerated checkout behavior.
 */
if (!customElements.get('hence-quiet-offer')) {
  customElements.define('hence-quiet-offer', class extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      clearTimeout(this.providerTimer);
      this.providerTimer = null;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (this.isConnected) this.refresh();
        });
      };
      this.handleChange = (event) => {
        const form = document.getElementById(this.dataset.formId);
        if (this.contains(event.target) || event.target.form === form) this.schedule();
      };
      // Read after provider event handlers and their next task have committed.
      // Some integrations update successful control properties without input/change.
      this.handleProviderInteraction = (event) => {
        const app = event.target.closest?.('.hq-app');
        if (!app || !this.contains(app)) return;
        clearTimeout(this.providerTimer);
        this.providerTimer = setTimeout(() => {
          this.providerTimer = null;
          cancelAnimationFrame(this.frame);
          this.frame = null;
          this.schedule();
        }, 0);
      };
      this.addEventListener('click', this.handleProviderInteraction, { capture: true, signal });
      this.addEventListener('change', this.handleProviderInteraction, { capture: true, signal });
      document.addEventListener('change', this.handleChange, { signal });
      document.addEventListener('input', this.handleChange, { signal });
      // FormData also fires for Dawn's Ajax submission. Capture it because the
      // event does not bubble; one-off must omit the key, not send an empty plan.
      this.addEventListener('formdata', (event) => {
        const form = document.getElementById(this.dataset.formId);
        if (event.target !== form || !this.querySelector('[data-native-cards]')) return;
        const selectedPlan = this.syncSellingPlan(form);
        event.formData.delete('selling_plan');
        if (selectedPlan && !selectedPlan.disabled && selectedPlan.value) {
          event.formData.set('selling_plan', selectedPlan.value);
        }
      }, { capture: true, signal });
      this.addEventListener('submit', (event) => {
        this.refresh();
        if (this.invalidSelection || this.kitBusy || this.kitReviewRequired || this.kitSelected) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (!this.invalidSelection && !this.kitBusy && !this.kitReviewRequired && this.kitSelected) this.addStarterPack();
        }
      }, { capture: true, signal });
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'checked', 'disabled'] });
      this.setupGallery(signal);
      this.schedule();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      clearTimeout(this.providerTimer);
      this.providerTimer = null;
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    syncSellingPlan(form) {
      const cards = this.querySelector('[data-native-cards]');
      if (!cards) return null;
      const radios = [...cards.querySelectorAll('input[type="radio"][name="selling_plan"]')]
        .filter(input => input.form === form);
      // Subify can insert a hidden plan into this form. Keep our associated
      // card radios; leave the provider's separate blocks and forms alone.
      form.querySelectorAll('input[name="selling_plan"]').forEach(input => {
        if (!radios.includes(input)) input.remove();
      });
      return radios.find(input => input.checked) || null;
    }

    async addStarterPack() {
      const form = document.getElementById(this.dataset.formId);
      const values = new FormData(form);
      const message = this.querySelector('[data-starter-message]');
      const link = this.querySelector('[data-starter-cart-link]');
      this.kitBusy = true;
      message.textContent = 'Adding your greens and Starter Pack…';
      message.hidden = false;
      link.hidden = true;
      this.refresh();
      try {
        await window.HenceStarterPack.add({
          root: window.Shopify.routes.root, kit: this.kit,
          variantId: Number(values.get('id')),
          productId: Number(this.querySelector('product-info').dataset.productId),
          quantity: Number(values.get('quantity'))
        });
        this.kitReviewRequired = true;
        message.textContent = 'Your items are in your basket. Review the total before payment.';
        link.hidden = false;
        window.location.assign(link.href);
      } catch (error) {
        this.kitReviewRequired = Boolean(error.reviewRequired);
        message.textContent = error.message;
        link.hidden = false;
      } finally {
        this.kitBusy = false;
        if (this.isConnected) this.refresh();
      }
    }

    setupGallery(signal) {
      const gallery = this.querySelector('[data-native-gallery]');
      if (!gallery) return;
      const panels = [...gallery.querySelectorAll('[data-gallery-panel]')];
      const buttons = [...gallery.querySelectorAll('[data-gallery-choice]')];
      const status = gallery.querySelector('[data-gallery-status]');
      let current = 0;
      const select = (index) => {
        current = (index + panels.length) % panels.length;
        panels.forEach((panel, i) => { panel.hidden = i !== current; });
        buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === current)));
        if (status) status.textContent = buttons[current].textContent.trim();
      };
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => select(index), { signal });
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          select(event.key === 'Home' ? 0 : event.key === 'End' ? panels.length - 1 : index + (event.key === 'ArrowRight' ? 1 : -1));
          buttons[current].focus();
        }, { signal });
      });
      let touch = null;
      gallery.addEventListener('touchstart', (event) => {
        touch = event.touches.length === 1 && event.target.closest('[data-gallery-panel]') ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      }, { passive: true, signal });
      gallery.addEventListener('touchend', (event) => {
        if (!touch) return;
        const dx = event.changedTouches[0].clientX - touch.x;
        const dy = event.changedTouches[0].clientY - touch.y;
        touch = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) select(current + (dx < 0 ? 1 : -1));
      }, { passive: true, signal });
      gallery.addEventListener('touchcancel', () => { touch = null; }, { passive: true, signal });
    }

    refresh() {
      const form = document.getElementById(this.dataset.formId);
      const source = this.querySelector('[data-offer-data]');
      const status = this.querySelector('[data-native-selection]');
      if (!form || !source || !status) return;
      let data;
      try { data = JSON.parse(source.textContent); } catch { return; }
      const selectedPlan = this.syncSellingPlan(form);
      const values = new FormData(form);
      const variant = data.variants[String(values.get('id'))];
      const planId = selectedPlan?.value || '';
      const plan = variant?.plans[planId];
      const cards = this.querySelector('[data-native-cards]');
      const fourWeek = variant?.plans['689245421902'];
      const quantity = Math.max(1, Number(values.get('quantity') || 1));
      const kitControl = this.querySelector('[data-starter-choice]');
      const kitPanel = this.querySelector('[data-starter-pack]');
      this.kit = data.starterPack;
      const kitAvailable = Boolean(this.kit && window.HenceStarterPack && !planId && variant?.available && !variant.requiresPlan);
      if (kitControl) {
        if (!kitAvailable) kitControl.checked = false;
        const disabled = !kitAvailable || Boolean(this.kitBusy || this.kitReviewRequired);
        if (kitControl.disabled !== disabled) kitControl.disabled = disabled;
        kitPanel.hidden = !kitAvailable;
      }
      this.kitSelected = Boolean(kitAvailable && kitControl?.checked);
      const kitCents = this.kitSelected ? this.kit.price : 0;
      const kitPaymentNote = this.querySelector('[data-starter-payment-note]');
      if (kitPaymentNote) kitPaymentNote.hidden = !this.kitSelected;
      const selection = this.querySelector('.hq-selection');
      if (selection) selection.inert = Boolean(this.kitBusy);
      form.setAttribute('aria-busy', String(Boolean(this.kitBusy)));
      const money = (cents) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
      if (cards) {
        const subscription = cards.querySelector('input[name="selling_plan"][value="689245421902"]');
        const once = cards.querySelector('input[name="selling_plan"][value=""]');
        const setDisabled = (input, next) => { if (input.disabled !== next) input.disabled = next; };
        setDisabled(subscription, !fourWeek || !variant?.available);
        setDisabled(once, !variant || variant.requiresPlan || !variant.available);
        cards.querySelectorAll('input[name="quantity"]').forEach(input => {
          const q = Number(input.value);
          setDisabled(input, !variant || q < variant.quantityMin || (variant.quantityMax != null && q > variant.quantityMax) || (q - variant.quantityMin) % variant.quantityIncrement !== 0);
        });
        const validPlan = planId === '689245421902' ? Boolean(fourWeek) : planId === '' && variant && !variant.requiresPlan;
        const selectedQuantity = cards.querySelector('input[name="quantity"]:checked');
        this.invalidSelection = !variant?.available || !validPlan || !selectedPlan || selectedPlan.disabled || !selectedQuantity || selectedQuantity.disabled || values.getAll('quantity').length !== 1;
        const warning = cards.querySelector('[data-card-unavailable]');
        warning.hidden = !this.invalidSelection;
        const warningText = this.invalidSelection ? 'This purchase selection is unavailable. Choose an available purchase type and pack count.' : '';
        if (warning.textContent !== warningText) warning.textContent = warningText;
        const set = (selector, text) => { const node = cards.querySelector(selector); if (node && node.textContent !== text) node.textContent = text; };
        const first = plan?.intro?.firstCents ?? plan?.priceCents;
        const isSubscription = planId === '689245421902' && Boolean(fourWeek);
        const trialValueCents = Number(this.dataset.trialValueCents || 950);
        set('[data-card-trial-value]', money(trialValueCents));
        const giftsOffered = !this.invalidSelection && data.giftVerified && data.extrasVerified && Boolean(fourWeek) && (this.dataset.giftPlan || '').split(',').map(id => id.trim()).includes('689245421902');
        const purchaseType = isSubscription ? 'monthly' : 'once';
        if (cards.dataset.purchase !== purchaseType) cards.dataset.purchase = purchaseType;
        cards.querySelectorAll('[data-plan-copy]').forEach(node => {
          const inactive = String(node.dataset.planCopy !== purchaseType);
          if (node.getAttribute('aria-hidden') !== inactive) node.setAttribute('aria-hidden', inactive);
        });
        const gifts = cards.querySelector('[data-card-gifts]');
        gifts.hidden = !giftsOffered;
        const validSubscription = isSubscription && !this.invalidSelection;
        set('[data-card-first]', this.invalidSelection ? '' : money((isSubscription ? first : variant.onceCents) * quantity + kitCents));
        cards.querySelector('[data-card-comparison]').hidden = !validSubscription;
        set('[data-card-original]', validSubscription ? money(variant.onceCents * quantity) : '');
        set('[data-card-renewal]', this.invalidSelection ? '' : isSubscription ? `Then ${money((plan.intro?.renewalCents ?? plan.priceCents) * quantity)} every 4 weeks` : 'No subscription.');
        const promotion = cards.querySelector('[data-card-saving]');
        const planFirst = fourWeek?.intro?.firstCents ?? fourWeek?.priceCents;
        promotion.hidden = !giftsOffered;
        set('[data-card-saving]', giftsOffered ? `Subscribe today for ${money(Math.max(0, variant.onceCents - planFirst) * quantity)} OFF and a FREE Starter Pack worth more than £20!` : '');
        const flexibility = cards.querySelector('[data-card-flexibility]');
        if (flexibility) flexibility.hidden = this.invalidSelection;
        const rewards = cards.querySelector('[data-card-rewards]');
        if (rewards) rewards.hidden = !giftsOffered;
        const quantities = [...cards.querySelectorAll('input[name="quantity"]')].filter(input => !input.disabled).map(input => Number(input.value));
        const widest = quantities.length ? Math.max(...quantities) : quantity;
        set('[data-card-guide-original]', variant ? money(variant.onceCents * widest) : '');
        set('[data-card-guide-first]', variant ? money(variant.onceCents * widest) : '');
        set('[data-card-guide-renewal]', fourWeek ? `Then ${money((fourWeek.intro?.renewalCents ?? fourWeek.priceCents) * widest)} every 4 weeks` : '');
        const payment = this.querySelector('.shopify-payment-button');
        if (payment) payment.hidden = this.invalidSelection || this.kitSelected || Boolean(this.kitBusy || this.kitReviewRequired);
      }
      const giftPlan = (this.dataset.giftPlan || '').split(',').map(id => id.trim()).includes(planId);
      let text = 'Choose your purchase option.';
      if (variant && !planId && !variant.requiresPlan) text = `${money(variant.onceCents * quantity)} once. ${quantity} ${quantity === 1 ? 'pack' : 'packs'}. One-off purchase.`;
      if (plan) text = plan.intro
        ? `${money(plan.intro.firstCents * quantity)} for your first order, then ${money(plan.intro.renewalCents * quantity)} on subsequent orders. ${quantity} ${quantity === 1 ? 'pack' : 'packs'}. ${plan.name}.`
        : `${money(plan.priceCents * quantity)} for this order. ${quantity} ${quantity === 1 ? 'pack' : 'packs'}. ${plan.name}.`;
      if (this.kitSelected) text = `${money(variant.onceCents * quantity + kitCents)} once. ${quantity} greens ${quantity === 1 ? 'pack' : 'packs'} and one £10 Starter Pack. No recurring payments. Discounts and delivery are confirmed in your basket and checkout.`;
      if (status.textContent !== text) status.textContent = text;
      const saving = this.querySelector('[data-native-saving]');
      if (saving) {
        saving.hidden = true;
        if (plan?.intro?.onceSaving) {
          const savingText = `Save ${plan.intro.onceSaving} per pack on your first subscription order compared with the ${variant.once} one-off pack price.`;
          if (saving.textContent !== savingText) saving.textContent = savingText;
        }
      }
      const value = this.querySelector('[data-native-value]');
      if (value) {
        const extras = data.giftVerified && data.extrasVerified && giftPlan && plan?.intro?.onceSaving;
        const heading = extras ? `Save ${money(plan.intro.onceSavingCents * quantity)} + free bottle worth ${data.bottleValue.replace(/\.00\b/g, '')}` : '';
        value.hidden = Boolean(cards) || !heading;
        if (heading && value.dataset.key !== heading) {
          const title = document.createElement('strong');
          title.textContent = heading;
          value.replaceChildren(title);
          value.dataset.key = heading;
        }
      }
      const actionPrice = this.querySelector('[data-native-action-price]');
      const available = !this.invalidSelection && !this.querySelector('.hq-actions button[name=add]')?.disabled;
      const currentPrice = available ? (plan ? money((plan.intro?.firstCents ?? plan.priceCents) * quantity) : !planId && variant && !variant.requiresPlan ? money(variant.onceCents * quantity + kitCents) : '') : '';
      if (actionPrice && actionPrice.textContent !== currentPrice) actionPrice.textContent = currentPrice;
      const gift = Boolean(!this.invalidSelection && data.giftVerified && plan && giftPlan);
      const selected = this.querySelector('[data-gift-selected]');
      const giftImage = this.querySelector('[data-gift-image]');
      const giftTerms = this.querySelector('[data-gift-terms]');
      const shippingImage = this.querySelector('[data-shipping-image]');
      const neutral = this.querySelector('[data-gift-neutral]');
      if (selected) selected.hidden = !gift;
      if (giftImage) giftImage.hidden = !gift;
      if (giftTerms) giftTerms.hidden = !gift;
      if (shippingImage) shippingImage.toggleAttribute('hidden', gift);
      if (neutral) neutral.hidden = gift;
    }
  });
}
